import * as React from 'react';
import { ServiceManager } from './ServiceManager';
import { ViewController } from '../../ViewController';
import ViewsManager, { ImportStatement } from './ViewsManager';
import { ErrorBoundary } from '@estee/elc-telemetry';

type ExportedViewState<T> = {
    viewController?: ViewController;
    component: React.ComponentClass<T> | undefined;
};

type RemoveViewController<T> = Omit<T, 'viewController'>;
type ExportedViewProps = {
    config?: object;
    translations?: object;
    viewController?: ViewController;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & { [key: string]: any };

export function convertToExportedView<IProps extends ExportedViewProps>(
    WrappedComponentImport: ImportStatement<IProps>,
    viewName: string,
    controllerKey?: string
) {
    return class extends React.Component<RemoveViewController<IProps>, ExportedViewState<IProps>> {
        static controllerName = controllerKey;

        constructor(props: RemoveViewController<IProps>) {
            super(props);

            const controller = ServiceManager.serviceHasStarted
                ? ServiceManager.getController(controllerKey)
                : undefined;

            this.state = {
                viewController: controller,
                component: ViewsManager.getView(viewName)
            };
        }

        public async componentDidMount() {
            try {
                if (!ServiceManager.serviceHasStarted) {
                    await ServiceManager.startService();
                }
                await ViewsManager.loadAndSaveModule(viewName, WrappedComponentImport);

                const viewController =
                    this.state.viewController || ServiceManager.getController(controllerKey);

                this.setState({
                    viewController,
                    component: ViewsManager.getView(viewName)
                });
            } catch (error) {
                console.error(error);
            }
        }

        render() {
            const Component = this.state.component as React.ComponentClass<ExportedViewProps>;

            if (!ServiceManager.serviceHasStarted || !Component) {
                return null;
            }

            return (
                <ErrorBoundary serviceName={ServiceManager.serviceName} name={viewName}>
                    <Component {...this.props} viewController={this.state.viewController} />
                </ErrorBoundary>
            );
        }
    };
}
